import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  Snackbar,
  TextField,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
} from "@mui/material";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import { useFormik } from "formik";
import * as yup from "yup";
import Labels from "../labels/Labels";
import { is } from "date-fns/locale";

export default function AddEmployer(props) {
  const [sftpUsername, setSftpUsername] = useState("");
  const createEmployeeAttributeMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().put(
        window.API_URL + `/employer`,
        values
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("employer");
        props.handleClose();
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: Labels.getLabel(
            "employer.errorCreating",
            "Error creating the employer, constraint violated."
          ),
        });
      },
    }
  );

  const validationSchema = yup.object({
    companyId: yup
      .string("Enter the company id")
      .required(
        Labels.getLabel("employer.companyIdRequired", "Company id is required")
      ),
    companyName: yup
      .string("Enter the name of the company")
      .required(
        Labels.getLabel(
          "employer.companyNameRequired",
          "Company name is required"
        )
      ),
    description: yup
      .string("Enter the description")
      .required(
        Labels.getLabel(
          "employer.descriptionRequired",
          "Description is required"
        )
      ),
    processorList: yup
      .array()
      .min(
        1,
        Labels.getLabel(
          "employer.processorRequired",
          "At least one processor is required"
        )
      ),
    bankList: yup
      .array()
      .min(
        1,
        Labels.getLabel(
          "employer.bankRequired",
          "At least one bank is required"
        )
      ),
  });

  const [enableSFTPCreation, setEnableSFTPCreation] = useState(false);

  const formik = useFormik({
    initialValues: {
      companyId: "",
      companyName: "",
      description: "",
      bank: "sutton",
      brand: "mastercard",
      processor: "qolo",
      sftpuser: "",
      isPaycardEnabled: false,
      isDigitalFullEnabled: false,
      isCorporateExpenseEnabled: false,
      isIncentiveCardEnabled: false,
      isVirtualCardEnabled: false,
      isVirtualAnonymousCardEnabled: false,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      createEmployeeAttributeMutation.mutate({ ...values });
    },
  });

  const queryClient = useQueryClient();

  async function fetchCompanyData() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/employer/all`
    );
    return response;
  }

  const { data, isLoading } = useQuery(["companies"], () => fetchCompanyData());
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const handleProcessorChange = (event, values) => {
    const newProcessor = event.target.value;
    formik.setFieldValue("processor", newProcessor);
    if (event.target.value === "visadps") {
      formik.setFieldValue("brand", "visa");
      formik.setFieldValue("bank", "sutton");
    }
  };

  const handleBrandChange = (event, values) => {
    const newBrand = event.target.value;
    formik.setFieldValue("brand", newBrand);
  };

  const handleBankChange = (event, values) => {
    const newBank = event.target.value;
    formik.setFieldValue("bank", newBank);
  };

  const handleNameChange = (event) => {
    const newName = event.target.value;
    formik.setFieldValue("companyName", newName);
    const formattedSftpuser =
      newName.toLowerCase().replace(/\s+/g, "") + "_sftpuser";
    setSftpUsername(formattedSftpuser);
  };

  return (
    <div>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        {isLoading ? (
          ""
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="companyId"
                label={Labels.getLabel("employer.companyId", "Company Id")}
                value={formik.values.companyId}
                onChange={formik.handleChange}
                error={
                  formik.touched.companyId && Boolean(formik.errors.companyId)
                }
                helperText={formik.touched.companyId && formik.errors.companyId}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="companyName"
                label={Labels.getLabel("employer.companyName", "Company Name")}
                value={formik.values.companyName}
                onChange={handleNameChange}
                error={
                  formik.touched.companyName &&
                  Boolean(formik.errors.companyName)
                }
                helperText={
                  formik.touched.companyName && formik.errors.companyName
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="description"
                label={Labels.getLabel("employer.description", "Description")}
                value={formik.values.description}
                onChange={formik.handleChange}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
              />
            </Grid>
            <Grid
              container
              spacing={2}
              alignContent={"center"}
              alignItems={"center"}
              alignSelf={"center"}
              justifyContent={"center"}
              paddingLeft={"1rem"}
              paddingTop={"1rem"}
            >
              <Grid item xs={4}>
                <FormControl>
                  <FormLabel id="processor-radio-buttons-group-label">
                    {Labels.getLabel("employer.processor", "Processor")}
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="processor-radio-buttons-group-label"
                    name="processor-radio-buttons-group"
                    onChange={handleProcessorChange}
                    value={formik.values.processor}
                  >
                    <FormControlLabel
                      value="qolo"
                      control={<Radio />}
                      label={Labels.getLabel("employer.qolo", "Qolo")}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl>
                  <FormLabel id="brand-radio-buttons-group-label">
                    {Labels.getLabel("employer.brand", "Brand")}
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="bank-radio-buttons-group-label"
                    name="brand-radio-buttons-group"
                    //onChange={handleBrandChange}

                    onChange={handleBrandChange}
                    value={formik.values.brand}
                  >
                    <FormControlLabel
                      value="visa"
                      control={<Radio />}
                      label={Labels.getLabel("employer.visa", "Visa")}
                    />
                    <FormControlLabel
                      value="mastercard"
                      control={<Radio />}
                      label={Labels.getLabel(
                        "employer.mastercard",
                        "Mastercard"
                      )}
                      disabled={formik.values.processor === "visadps"}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl>
                  <FormLabel id="bank-radio-buttons-group-label">
                    {Labels.getLabel("employer.bank", "Bank")}
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="bank-radio-buttons-group-label"
                    name="bank-radio-buttons-group"
                    //onChange={handleBankChange}
                    onChange={handleBankChange}
                    value={formik.values.bank}
                  >
                    <FormControlLabel
                      value="sutton"
                      control={<Radio />}
                      label={Labels.getLabel(
                        "employer.suttonBank",
                        "Sutton Bank"
                      )}
                    />
                    <FormControlLabel
                      value="republic"
                      control={<Radio disabled />}
                      label={Labels.getLabel(
                        "employer.republicBank",
                        "Republic Bank"
                      )}
                      disabled={formik.values.processor === "visadps"}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
            <Grid
              container
              spacing={2}
              alignContent={"center"}
              alignItems={"center"}
              alignSelf={"center"}
              justifyContent={"center"}
              paddingLeft={"1rem"}
              paddingTop={"1rem"}
            >
              <Grid item xs={12}>
                <FormControl variant="standard" fullWidth>
                  <FormLabel id="product-checkbox-group-label">
                    {Labels.getLabel("employer.products", "Product(s)")}
                  </FormLabel>
                  <Grid container spacing={2} alignContent={"center"}>
                    <Grid item xs={4} paddingTop={"1rem"}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={(e) => {
                              formik.setFieldValue(
                                "isPaycardEnabled",
                                e.target.checked
                              );
                              console.log("clicked", e.target.checked);
                            }}
                            value={formik.values.isPaycardEnabled}
                            name="isPaycardEnabled"
                            disabled={formik.values.brand === "visa"}
                          />
                        }
                        label={Labels.getLabel("employer.paycard", "Paycard")}
                      />
                    </Grid>
                    <Grid item xs={4} paddingTop={"1rem"}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={(e) => {
                              formik.setFieldValue(
                                "isDigitalFullEnabled",
                                e.target.checked
                              );
                            }}
                            value={formik.values.isDigitalFullEnabled}
                            name="isDigitalFullEnabled"
                            disabled={formik.values.brand === "visa"}
                          />
                        }
                        label={Labels.getLabel(
                          "employer.digitalFull",
                          "Digital Full"
                        )}
                      />
                    </Grid>
                    <Grid item xs={4} paddingTop={"1rem"}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={(e) => {
                              formik.setFieldValue(
                                "isCorporateExpenseEnabled",
                                e.target.checked
                              );
                            }}
                            value={formik.values.isDigitalFullEnabled}
                            name="isCorporateExpenseEnabled"
                            disabled={formik.values.brand === "mastercard"}
                          />
                        }
                        label={Labels.getLabel(
                          "employer.corporateExpense",
                          "Corporate Expense"
                        )}
                      />
                    </Grid>
                    <Grid item xs={4} paddingTop={"1rem"}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={(e) => {
                              formik.setFieldValue(
                                "isIncentiveCardEnabled",
                                e.target.checked
                              );
                            }}
                            value={formik.values.isIncentiveCardEnabled}
                            name="isIncentiveCardEnabled"
                            disabled={formik.values.brand === "visa"}
                          />
                        }
                        label={Labels.getLabel(
                          "employer.incentiveCard",
                          "Incentive Card"
                        )}
                      />
                    </Grid>
                    <Grid item xs={4} paddingTop={"1rem"}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={(e) => {
                              formik.setFieldValue(
                                "isVirtualCardEnabled",
                                e.target.checked
                              );
                            }}
                            value={formik.values.isVirtualCardEnabled}
                            name="isVirtualCardEnabled"
                            disabled={formik.values.brand === "visa"}
                          />
                        }
                        label={Labels.getLabel(
                          "employer.virtualCard",
                          "Virtual Card"
                        )}
                      />
                    </Grid>
                    <Grid item xs={4} paddingTop={"1rem"}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={(e) => {
                              formik.setFieldValue(
                                "isVirtualAnonymousCardEnabled",
                                e.target.checked
                              );
                            }}
                            value={formik.values.isVirtualAnonymousCardEnabled}
                            name="isVirtualAnonymousCardEnabled"
                            disabled={formik.values.brand === "visa"}
                          />
                        }
                        label={Labels.getLabel(
                          "employer.anonymousCard",
                          "Virtual Anonymous"
                        )}
                      />
                    </Grid>
                  </Grid>
                </FormControl>
              </Grid>
            </Grid>
            <Grid
              container
              spacing={2}
              alignContent={"center"}
              alignItems={"center"}
              alignSelf={"center"}
              justifyContent={"center"}
              paddingLeft={"1rem"}
              paddingTop={"1rem"}
            >
              <Grid item xs={5}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onClick={() => {
                          setEnableSFTPCreation(!enableSFTPCreation);
                          if (!enableSFTPCreation) {
                            // When enabling SFTP creation, set the sftpuser field to the formatted username
                            formik.setFieldValue("sftpuser", sftpUsername);
                          } else {
                            // When disabling SFTP creation, clear the sftpuser field
                            formik.setFieldValue("sftpuser", "");
                          }
                        }}
                      />
                    }
                    label={Labels.getLabel(
                      "employer.sftpAccount",
                      "SFTP account"
                    )}
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={7}>
                {enableSFTPCreation ? (
                  <TextField
                    fullWidth
                    onFocus={(e) => e.target.select()}
                    disabled={!enableSFTPCreation}
                    id="sftpuser"
                    label={Labels.getLabel(
                      "employer.desiredSftpUsername",
                      "Desired SFTP username"
                    )}
                    value={formik.values.sftpuser}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.sftpuser && Boolean(formik.errors.sftpuser)
                    }
                    helperText={
                      formik.touched.sftpuser && formik.errors.sftpuser
                    }
                  />
                ) : (
                  ""
                )}
              </Grid>
            </Grid>

            <Grid item xs={12} pt={3}>
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={createEmployeeAttributeMutation.isLoading}
              >
                {Labels.getLabel("employer.save", "SAVE")}
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
